import React, { JSX, useState, useEffect, useRef } from 'react';
import {
  LinkField,
  Link,
  Text,
  RichText,
  Field,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import Icons from 'components/atoms/Icons';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { ItemValue } from 'components/types/ListItem';

type CTASelectionItem = {
  id: string;
  fields: {
    Title: Field<string>;
    Body1: RichTextField;
    CTA1: LinkField;
    Body2: RichTextField;
    CTA2: LinkField;
  };
};

type DynamicCTAListProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Headline: Field<string>;
    Description: RichTextField;
    Background: Field<boolean>;
    BackgroundColor: ItemValue;
    ['CTA Selections']: Array<CTASelectionItem>;
  };
};
const DynamicCTAList = (props: DynamicCTAListProps): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );
  const {
    Headline,
    Description,
    Background,
    BackgroundColor,
    ['CTA Selections']: CTASelections,
  } = props.fields;
  const [selectedItem, setSelectedItem] = useState<CTASelectionItem | null>(
    null
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  if (!props || !props.fields) {
    return <div></div>;
  }

  const hasBackground = Background && Background?.value === true;

  const selectItem = (item: CTASelectionItem) => {
    setSelectedItem(item);
    setDropdownOpen(false);
  };
  const hasOption1 =
    selectedItem &&
    (selectedItem.fields.Body1?.value || selectedItem.fields.CTA1?.value?.href);
  const hasOption2 =
    selectedItem &&
    (selectedItem.fields.Body2?.value || selectedItem.fields.CTA2?.value?.href);
  const isSingleOption =
    (hasOption1 && !hasOption2) || (!hasOption1 && hasOption2);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div
      className="overhauled"
      id={`dynamic-cta-parent-${props.rendering?.uid}`}
    >
      <div
        className={classNames(
          'dynamic-cta-list',
          marginSize,
          hasBackground ? BackgroundColor?.fields?.heading?.value : ''
        )}
      >
        <div className="jss-inner-container">
          <div className="dynamic-cta-list-content inner-vertical-spacing">
            {Headline && (
              <Text
                className="dynamic-cta-list-headline"
                tag="h2"
                field={Headline}
              />
            )}
            {Description && (
              <RichText
                className="osf-body-copy dynamic-cta-list-description"
                field={Description}
              />
            )}
            {CTASelections && CTASelections.length > 0 && (
              <div className="dynamic-cta-dropdown-wrapper">
                <div
                  ref={dropdownRef}
                  className={`${dropdownOpen ? 'cta-selector open' : 'cta-selector'}`}
                >
                  <button
                    className="selector"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <p className="default">
                      {selectedItem
                        ? selectedItem.fields.Title?.value
                        : 'Select an option'}
                    </p>
                    {dropdownOpen ? (
                      <Icons id="chevron-up" />
                    ) : (
                      <Icons id="chevron-down" />
                    )}
                  </button>
                  <div
                    className={`${dropdownOpen ? 'cta-options shown' : 'cta-options'}`}
                  >
                    {CTASelections.map((item) => {
                      return (
                        <p
                          key={item.id}
                          onClick={e => {
                            e.stopPropagation();
                            selectItem(item);
                          }}
                          onKeyDown={e => {
                            if (e.key == 'Enter' || e.key == ' ') {
                              e.preventDefault();
                              selectItem(item);
                            }
                          }}
                          tabIndex={dropdownOpen ? 0 : -1}
                        >
                          {item.fields.Title?.value}
                        </p>
                      );
                    })}
                  </div>
                </div>

                {selectedItem && (
                  <div
                    className={`dynamic-cta-selected-content ${isSingleOption ? 'single-option' : ''}`}
                  >
                    {hasOption1 && (
                      <div className="dynamic-cta-option">
                        {selectedItem.fields.Body1 && (
                          <RichText
                            className="osf-body-copy"
                            field={selectedItem.fields.Body1}
                          />
                        )}
                        {selectedItem.fields.CTA1 &&
                          selectedItem.fields.CTA1.value?.href && (
                            <Link
                              className="btn-secondary"
                              field={selectedItem.fields.CTA1}
                            />
                          )}
                      </div>
                    )}
                    {hasOption2 && (
                      <div className="dynamic-cta-option">
                        {selectedItem.fields.Body2 && (
                          <RichText
                            className="osf-body-copy"
                            field={selectedItem.fields.Body2}
                          />
                        )}
                        {selectedItem.fields.CTA2 &&
                          selectedItem.fields.CTA2.value?.href && (
                            <Link
                              className="btn-secondary"
                              field={selectedItem.fields.CTA2}
                            />
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicCTAList;
